import { useMemo } from 'react';

import type { HomeWidgetsPriority } from '../../../components/widgets/homeWidgets/types';
import { useFlag } from '../../../services/launchDarkly';
import type { Widget, WidgetEntry, WidgetSizeType } from '../types';
import { sortWidgets } from '../utils';

import { useBingoWidgets } from './useBingoWidgets';
import { useChallengeWidgets } from './useChallengeWidgets';
import { useCoinBackWidgets } from './useCoinBackWidgets';
import { useEventTriggerWidgets } from './useEventTriggerWidgets';
import { useFreeSpinWidgets } from './useFreeSpinWidgets';
import { useMetagameWidgets } from './useMetagameWidgets';
import { useMiniGameWidgets } from './useMiniGameWidgets';
import { useMonetizationWidgets } from './useMonetizationWidgets';
import { usePrizeDropWidgets } from './usePrizeDropWidgets';
import { useQuestWidgets } from './useQuestWidgets';
import { useSnappyGiftWidgets } from './useSnappyGiftWidgets';
import { useTournamentWidgets } from './useTournamentWidgets';
import { useUpcomingDailyMissionWidgets } from './useUpcomingDailyMissionWidgets';
import { useUpcomingMissionWidgets } from './useUpcomingMissionWidgets';
import { useWidgetLayout } from './useWidgetLayout';

export function useWidgets() {
  const homeWidgetsOrder = useFlag<HomeWidgetsPriority | undefined>('home_widgets_order', undefined);
  const bingoWidgets = useBingoWidgets();
  const challengeWidgets = useChallengeWidgets();
  const eventTriggerWidgets = useEventTriggerWidgets();
  const metagameWidgets = useMetagameWidgets();
  const snappyGiftWidgets = useSnappyGiftWidgets();
  const questWidgets = useQuestWidgets();
  const freeSpinWidgets = useFreeSpinWidgets();
  const miniGameWidgets = useMiniGameWidgets();
  const coinBackWidgets = useCoinBackWidgets();
  const prizeDropWidgets = usePrizeDropWidgets();
  const tournamentWidgets = useTournamentWidgets();
  const upcomingDailyMissionWidgets = useUpcomingDailyMissionWidgets();
  const monetizationWidgets = useMonetizationWidgets();
  const upcomingMissionWidgets = useUpcomingMissionWidgets();

  const widgetsData: WidgetEntry[] = useMemo(
    () => [
      ...bingoWidgets,
      ...challengeWidgets,
      ...coinBackWidgets,
      ...eventTriggerWidgets,
      ...freeSpinWidgets,
      ...miniGameWidgets,
      ...metagameWidgets,
      ...prizeDropWidgets,
      ...questWidgets,
      ...snappyGiftWidgets,
      ...tournamentWidgets,
      ...upcomingDailyMissionWidgets,
      ...upcomingMissionWidgets,
      ...monetizationWidgets,
    ],
    [
      bingoWidgets,
      challengeWidgets,
      coinBackWidgets,
      eventTriggerWidgets,
      freeSpinWidgets,
      metagameWidgets,
      miniGameWidgets,
      monetizationWidgets,
      prizeDropWidgets,
      questWidgets,
      snappyGiftWidgets,
      tournamentWidgets,
      upcomingDailyMissionWidgets,
      upcomingMissionWidgets,
    ],
  );

  const widgetsCount = widgetsData.length;

  const { containerRef, widgetSizeTypes, allWidgetsVisible } = useWidgetLayout({ widgetsCount });

  const widgets: Widget[] = useMemo(() => {
    const unsorted: Widget[] = widgetsData.map((widgetData) => ({
      id: widgetData.id,
      sizeType: 'small' as WidgetSizeType,
      type: widgetData.type,
      data: widgetData.data,
    }));

    const sorted = sortWidgets(unsorted, homeWidgetsOrder);

    return sorted.map((widget, i) => ({
      ...widget,
      sizeType: widgetSizeTypes[i] ?? ('small' as WidgetSizeType),
    }));
  }, [homeWidgetsOrder, widgetSizeTypes, widgetsData]);

  return { widgets, containerRef, allWidgetsVisible };
}
